import _ from 'lodash';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { StorySymbols, CardData } from 'types/story';
import { PARAMS } from 'common/constants';
import { IFRAME_ACTIONS, transmitTo } from 'common/utils/iframe-tunnel';
import { CardFacade } from 'common/utils/facades/card-facade';
import { parseURL, searchObjectToString } from 'common/utils/parse-url';
import { ISelectionHintContext } from 'client/components/common/SelectionHint/SelectionHintContext';
import CardRenderer from './CardRenderer';
import css from './StoryCard.scss';

interface NavProps extends RouteComponentProps {
	nextDefaultCardId: Props['nextDefaultCardId'];
	data: Props['data'];
}

const Navigation = withRouter((props: NavProps) => {
	const { nextDefaultCardId, data, history } = props;
	const card = new CardFacade(data);
	const { events } = card;

	const getNextCardSearchObject = ({ nextCardId }: { nextCardId: string }) => {
		if (!nextCardId) {
			return null;
		}

		const { searchObject } = parseURL(window.location.href);

		_.set(searchObject, PARAMS.CARD_ID, nextCardId);

		return searchObject;
	};

	return (
		<div className={css.navButtons}>
			{_.map(events, ({ _id, name, connection }) => {
				const connectionPointId = _.get(connection, 'pointId');
				const nextCardId = connectionPointId || nextDefaultCardId;
				const nextCardSearchObject = getNextCardSearchObject({ nextCardId })!;
				const search = nextCardSearchObject ? searchObjectToString(nextCardSearchObject) : '';

				const nextCard = () => {
					history.push({ search });
					transmitTo({
						id: 'StoryCard',
						target: 'admin',
						action: IFRAME_ACTIONS.UPDATE_ADMIN_PREVIEW_LOCATION,
						payload: nextCardSearchObject,
					});
				};

				if (!nextCardId) {
					return null;
				}

				return (
					<button type="button" className={css.navButton} onClick={nextCard} key={_id}>
						{[name, _.get(connection, 'type'), nextCardId].join(' – ')}
					</button>
				);
			})}
		</div>
	);
});

const defaultProps = {
	symbols: {},
};

export type Props = {
	isEditor: boolean;
	treeOptions:
		| { target: 'default'; isCardTree: true }
		| ({ target: 'editor'; isCardTree: true } & ISelectionHintContext['editor']['treeOptions']);
	data: CardData;
	nextDefaultCardId: string;
	symbols?: StorySymbols;
} & typeof defaultProps;

export class StoryCard<T = void> extends React.PureComponent<T & Props, any> {
	static defaultProps = defaultProps;

	renderCard() {
		return (
			<CardRenderer
				symbols={this.props.symbols}
				data={this.props.data}
				treeOptions={this.props.treeOptions}
				isEditor={this.props.isEditor}
			/>
		);
	}

	renderNavigation() {
		return (
			!this.props.isEditor && (
				<Navigation nextDefaultCardId={this.props.nextDefaultCardId} data={this.props.data} />
			)
		);
	}

	render() {
		return (
			<div className="story-card">
				{this.renderCard()}
				{this.renderNavigation()}
			</div>
		);
	}
}
